import React from "react";
import { IconSvg } from "monica-alexandria";
import styled, { css } from "styled-components";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const read_date_key = (dateValue) => {
  const dateItem = new Date(dateValue);
  const year = `${dateItem.getFullYear()}`;
  const month = `${dateItem.getMonth() + 1}`.padStart(2, "0");
  const day = `${dateItem.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const read_date_start = (dateValue) => {
  const dateItem = new Date(dateValue);

  dateItem.setHours(0, 0, 0, 0);

  return dateItem;
};

const Component = styled.div`
  width: 100%;
  position: relative;

  .CalendarFieldLabel {
    position: absolute;
    top: 1.8rem;
    left: 1.6rem;
    transform: translateY(-1rem);
    color: var(--greyDark);
    font-size: var(--smallText);
    z-index: 2;
    pointer-events: none;
  }

  .CalendarFieldRequired {
    position: absolute;
    top: 0.8rem;
    right: 0.8rem;
    z-index: 2;
    pointer-events: none;

    svg {
      width: var(--smallIcon);
      fill: var(--warning);
    }
  }

  .react-datepicker-wrapper {
    display: block;
    width: 100%;
  }

  .react-datepicker__input-container {
    display: block;
    width: 100%;
  }

  input {
    width: 100%;
    border: none;
    background: ${(props) => props.theme.background};
    color: ${(props) => props.theme.color};
    box-shadow: ${(props) => props.theme.in};
    border-radius: var(--smallRadius);
    transition-property: box-shadow;
    transition-duration: 0.5s;
    padding: var(--normalPads);
    padding-top: 3rem;
    font-size: var(--normalText);
    box-sizing: border-box;

    &::placeholder {
      color: inherit;
      opacity: 0.5;
      white-space: nowrap;
      width: 100%;
      overflow: hidden;
    }

    &:focus {
      outline: 0;
      box-shadow: ${(props) => props.theme.inFocus};
    }
  }

  .react-datepicker {
    font-family: "fontMedium", sans-serif;
    font-size: var(--normalText);
    background-color: ${(props) => props.theme.background};
    background: ${(props) => props.theme.background};
    color: ${(props) => props.theme.color};
    border-radius: var(--smallRadius);
    display: grid;
    grid-template-columns: 23rem auto;
    align-items: start;
    position: relative;
    box-shadow: ${(props) => props.theme.out};
    border: none;
    line-height: initial;
  }

  .react-datepicker__month-container {
    width: 23rem;
  }

  .react-datepicker h2 {
    font-size: 1.5rem;
    color: ${(props) => props.theme.color};
  }

  .react-datepicker__navigation {
    align-items: flex-end;
  }

  .react-datepicker__time {
    background: ${(props) => props.theme.background} !important;
    color: ${(props) => props.theme.color} !important;
  }

  .react-datepicker__time-container {
    border-left: 1px solid var(--grey);
  }

  .react-datepicker__time,
  .react-datepicker__time-box,
  .react-datepicker__time-list {
    width: 100% !important;
  }

  .react-datepicker__time-box {
    overflow-y: auto !important;
  }

  .react-datepicker__time-list {
    margin: 0;
    padding: 0;
    overflow-y: visible !important;
  }

  .react-datepicker__day {
    background: ${(props) => props.theme.background};
    color: ${(props) => props.theme.color};
    width: 3rem;
    text-align: center;
    padding: 1rem 0 1rem 0;
    margin: 0;
  }

  .react-datepicker__time-list-item:hover,
  .react-datepicker__day:hover {
    background: ${(props) => props.theme.low} !important;
    background-color: ${(props) => props.theme.low} !important;
  }

  .react-datepicker__day--disabled {
    color: ${(props) => props.theme.mid};
    opacity: 0.45;
    cursor: not-allowed;
  }

  .react-datepicker__day--disabled:hover {
    background: ${(props) => props.theme.background} !important;
    background-color: ${(props) => props.theme.background} !important;
  }

  .react-datepicker__time-list-item--disabled {
    display: none;
  }

  .react-datepicker__header {
    background: ${(props) => props.theme.background};
    border-bottom: 1px solid var(--grey);
    overflow: hidden;
    border-top-left-radius: var(--smallRadius);
    border-top-right-radius: var(--smallRadius);
    padding: var(--normalPads);
    position: relative;
  }

  .react-datepicker__day-name {
    color: ${(props) => props.theme.color};
    font-size: 1.5rem;
    width: 3rem;
    margin: 0;
    padding: 0;
  }

  .react-datepicker-time__header {
    color: ${(props) => props.theme.color};
    font-size: 1.5rem;
  }

  .react-datepicker__day-names {
    margin-bottom: 0;
    padding-top: 1rem;
  }

  .react-datepicker__day--selected {
    background: var(--main);
    color: var(--white);
  }

  .react-datepicker__time-list-item--selected:hover,
  .react-datepicker__day--selected:hover {
    background-color: var(--main) !important;
    background: var(--main) !important;
  }

  .react-datepicker__time-list-item--selected {
    background-color: var(--main) !important;
    background: var(--main) !important;
    color: var(--white);
  }

  ${(props) => props.excludeWeekends ? css`
    .react-datepicker__day-name--weekend,
    .react-datepicker__day--weekend {
      display: none;
    }
  ` : ""}
`;

/**
 * Shared date-time field with explicit hour bounds for local app flows.
 */
export const Calendar_Field = (props) => {
  const selectedDate = props.selected ? new Date(props.selected) : props.defaultDate ? new Date(props.defaultDate) : new Date();
  const interval = props.interval || 5;
  const minHour = props.minHour || 0;
  const maxHour = props.maxHour || 23;
  const minTime = new Date(selectedDate);
  const maxTime = new Date(selectedDate);
  const minDate = props.minDate ? read_date_start(props.minDate) : null;
  const minDateKey = props.minDate ? read_date_key(props.minDate) : "";
  const maxDate = props.maxDate ? new Date(props.maxDate) : null;
  const maxDateKey = props.maxDate ? read_date_key(props.maxDate) : "";
  const blockedDateLookup = (props.blockedDates || []).reduce((lookup, dateValue) => {
    return {
      ...lookup,
      [dateValue]: true
    };
  }, {});
  const excludeWeekends = Boolean(props.excludeWeekends);

  if (!props.selected && !props.defaultDate) {
    selectedDate.setHours(maxHour, 0, 0, 0);
  }

  minTime.setHours(minHour, 0, 0, 0);
  maxTime.setHours(maxHour, 0, 0, 0);

  return (
    <Component excludeWeekends={excludeWeekends}>
      <label className="CalendarFieldLabel">{props.label}</label>
      {props.required ? (
        <span className="CalendarFieldRequired">
          <IconSvg Icon="Asterisk" />
        </span>
      ) : null}
      <DatePicker
        selected={props.selected}
        openToDate={selectedDate}
        preSelection={selectedDate}
        onChange={(date) => props.onChange(date)}
        filterDate={(dateValue) => {
          const dateKey = read_date_key(dateValue);
          const dayValue = new Date(dateValue).getDay();
          const isWeekend = dayValue === 0 || dayValue === 6;
          const isAfterMinDate = !minDateKey || dateKey >= minDateKey;
          const isBeforeMaxDate = !maxDateKey || dateKey <= maxDateKey;

          return !blockedDateLookup[dateKey] && (!excludeWeekends || !isWeekend) && isAfterMinDate && isBeforeMaxDate;
        }}
        minDate={minDate}
        maxDate={maxDate}
        showTimeSelect
        timeFormat="HH:mm"
        timeIntervals={interval}
        minTime={minTime}
        maxTime={maxTime}
        filterTime={(timeValue) => {
          const hour = new Date(timeValue).getHours();

          return hour >= minHour && hour <= maxHour;
        }}
        dateFormat="MMMM d, yyyy h:mm aa"
        placeholderText={props.placeholder}
      />
    </Component>
  );
};
